import { logger } from './logger';

export type FaviconColor = 'green' | 'yellow' | 'red';

/**
 * Get the appropriate favicon color based on cost and thresholds
 * @param cost Current meeting cost
 * @param yellowThreshold Threshold for yellow (cost >= yellowThreshold)
 * @param redThreshold Threshold for red (cost >= redThreshold)
 * @returns Color to use for favicon/logo
 */
export function getFaviconColor(
  cost: number,
  yellowThreshold: number,
  redThreshold: number
): FaviconColor {
  if (cost >= redThreshold) {
    return 'red';
  }
  if (cost >= yellowThreshold) {
    return 'yellow';
  }
  return 'green';
}

/**
 * Update the favicon link in the DOM
 * @param color Color variant to use ('green', 'yellow', or 'red')
 */
export function updateFavicon(color: FaviconColor): void {
  if (typeof document === 'undefined') return;

  const faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
  if (faviconLink) {
    const filename = color === 'green' ? 'favicon.svg' : `favicon-${color}.svg`;
    faviconLink.href = `/${filename}`;
  }
}

/**
 * Update the apple-touch-icon (logo) link in the DOM
 * @param color Color variant to use ('green', 'yellow', or 'red')
 */
export function updateLogo(color: FaviconColor): void {
  if (typeof document === 'undefined') return;

  const logoLink = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
  if (logoLink) {
    const filename = color === 'green' ? 'logo.svg' : `logo-${color}.svg`;
    logoLink.href = `/${filename}`;
  }
}

/**
 * Update both favicon and logo based on cost and thresholds
 * @param cost Current meeting cost
 * @param yellowThreshold Threshold for yellow
 * @param redThreshold Threshold for red
 */
export function updateFaviconAndLogo(
  cost: number,
  yellowThreshold: number,
  redThreshold: number
): void {
  const color = getFaviconColor(cost, yellowThreshold, redThreshold);
  updateFavicon(color);
  updateLogo(color);
}

/**
 * Check if a service worker is registered
 * @returns Promise that resolves to true if a service worker is registered
 */
export async function hasServiceWorker(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }
  
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const hasActiveSW = registrations.length > 0 && registrations.some(reg => reg.active !== null);
    
    if (registrations.length > 0) {
      logger.debug('Service Worker status', {
        count: registrations.length,
        active: hasActiveSW,
        registrations: registrations.map(reg => ({
          scope: reg.scope,
          state: reg.active?.state || 'none'
        }))
      });
    }
    
    return hasActiveSW;
  } catch (error) {
    logger.warn('Error checking service worker', error);
    return false;
  }
}

/**
 * Check if the Badge API is available
 * @returns true if Badge API is supported
 */
export function isBadgeAPIAvailable(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  // Check if Badge API is available
  const hasBadgeAPI = 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
  
  // Badge API typically requires a service worker or installed PWA
  // Check if we're in a secure context (HTTPS or localhost)
  const isSecureContext = typeof window !== 'undefined' && window.isSecureContext;
  
  return hasBadgeAPI && isSecureContext;
}

/**
 * Set the app badge with a number
 * @param value Number to display in the badge (0-99, or undefined to clear)
 */
export async function setAppBadge(value?: number): Promise<void> {
  if (!isBadgeAPIAvailable()) {
    logger.debug('Badge API not available');
    return;
  }

  try {
    if (value === undefined || value === 0) {
      await navigator.clearAppBadge();
      logger.debug('Badge cleared');
    } else {
      // Badge API supports numbers up to 99,999 but displays differently
      // For better UX, we'll cap at 99 and show "99+" for higher values
      // Per MDN: passing 0 clears the badge, so we ensure value > 0
      const badgeValue = Math.min(Math.max(Math.round(value), 1), 99);
      await navigator.setAppBadge(badgeValue);
      logger.debug('Badge set', { value: badgeValue, originalValue: value });
    }
  } catch (error) {
    logger.warn('Badge API error when setting badge', error);
  }
}

/**
 * Clear the app badge
 */
export async function clearAppBadge(): Promise<void> {
  if (!isBadgeAPIAvailable()) {
    logger.debug('Badge API not available for clearing');
    return;
  }

  try {
    await navigator.clearAppBadge();
    logger.debug('Badge cleared');
  } catch (error) {
    logger.warn('Badge API error when clearing', error);
  }
}

/**
 * Update the app badge based on meeting cost
 * 
 * Note: Badges only appear on installed PWAs, not in browser tabs.
 * The user must install the app as a PWA to see badges.
 * 
 * @param cost Current meeting cost
 * @param isRunning Whether the timer is currently running
 */
export async function updateAppBadge(cost: number, isRunning: boolean): Promise<void> {
  if (!isBadgeAPIAvailable()) {
    logger.debug('Badge API not available');
    return;
  }

  // Check if service worker is registered (required for Badge API)
  const swRegistered = await hasServiceWorker();
  if (!swRegistered) {
    logger.debug('Service Worker not registered - Badge API requires an installed PWA');
    return;
  }

  try {
    // Only show badge if timer is running and cost > 0
    if (isRunning && cost > 0) {
      logger.debug('Updating badge', { cost, isRunning });
      await setAppBadge(cost);
    } else {
      logger.debug('Clearing badge', { cost, isRunning });
      await clearAppBadge();
    }
  } catch (error) {
    logger.error('Error updating app badge', error);
    if (error instanceof Error) {
      logger.error('Error details', {
        message: error.message,
        name: error.name,
        hasServiceWorker: swRegistered
      });
    }
  }
}
